import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, SAVE_TYPES } from '~/utils/constants/common';
import { handleApiError, notify, cast, generatePersonName } from '~/utils/helper';
import { CloseIcon } from '~/assets/icons';
import { IEditInvoice, IInvoice } from '~/utils/interface/invoice';
import { BaseDatePicker, BaseRichEditor, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import InvoiceListItem from '~/components/invoices/invoice-list-item/InvoiceListItem';
import ClientInformation from '~/components/estimate/client-information/ClientInformation';
import { IJob } from '~/utils/interface/job';
import styles from './EditInvoiceDialog.module.scss';
import classNames from 'classnames/bind';
import PaymentCalculation from '~/components/estimate/payment-calculation/PaymentCalculation';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleCreateInvoice, handleUpdateInvoice } from '~/thunks/invoice/invoiceThunk';
import useQuickbook from '~/hooks/useQuickbook';
import { STATUSES } from '~/utils/constants/invoice';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_UNITS } from '~/utils/constants/master';
import { handleGetQuickbookCustomers } from '~/thunks/quickbook/quickbookThunk';
import { CUSTOMER_TYPES_CONTACT } from '~/utils/constants/customers';
import { commonActions } from '~/thunks/common/commonSlice';

type EditInvoiceDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: string;
  data?: IJob;
  updateData: () => void;
  editData?: IInvoice;
};

const cx = classNames.bind(styles);
const initialValues: IEditInvoice = {
  date: dayjs().toISOString(),
  subtotal: 0,
  tax: 0,
  total: 0,
  description: '',
  isPushOnQuickbook: false,
  name: '',
};

const EditInvoiceDialog = (props: EditInvoiceDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'admin/common']);
  const dispatch = useAppDispatch();
  const { quickbook } = useQuickbook();
  const { quickbookCustomers } = useAppSelector((state) => state.quickbook);

  const form = useFormik({
    validateOnChange: false, // Prevent form reset API error
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await dispatch(handleCreateInvoice({ ...values, jobId: String(props?.data?.id) })).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
          if (props.updateData) props.updateData();
        } catch (e) {
          handleApiError(e, setFieldError);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await dispatch(handleUpdateInvoice({ id: String(props.editData?.id), data: values })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));

          handleSetOpen(false, true);
          if (props.updateData) props.updateData();
        } catch (e) {
          handleApiError(e, setFieldError);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }
    },
  });

  const statusOptions = useMemo(
    () =>
      STATUSES.map((status: string) => ({
        id: status,
        name: t(`statuses.${status}`),
      })),
    [t]
  );
  //#endregion Declare Hook

  //#region Selector
  const {
    profile: { tax },
  } = useAppSelector((state) => state.auth);

  //#endregion Selector

  //#region Declare State
  const [companyTax, setCompanyTax] = useState<number>(0);
  const [customerQuickbookOption, setCustomerQuickbookOption] = useState<string>(SAVE_TYPES.NO);
  const newOrExistingOptionalOption = useMemo(
    () => [
      {
        id: SAVE_TYPES.NO,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NO}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.NEW,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NEW}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.EXIST,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.EXIST}`, { ns: 'admin/common' }),
      },
    ],
    [t]
  );
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.editData) {
      form.setFieldValue('status', cast(props.editData.status));
      form.setFieldValue('date', cast(props.editData.date));
      form.setFieldValue('expireDate', cast(props.editData.expireDate));
      form.setFieldValue('description', cast(props.editData.description));
      form.setFieldValue('total', cast(props.editData.total));
      form.setFieldValue('subtotal', cast(props.editData.subtotal));
      form.setFieldValue('tax', cast(props.editData.tax));
      form.setFieldValue('name', cast(props.editData.name));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editData]);

  useEffect(() => {
    form.setFieldValue('tax', cast(companyTax));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyTax]);
  useEffect(() => {
    if (props.open && !props.editData) {
      form.setFieldValue('name', props?.data?.name);
    }

    if (props.open && quickbook?.isQuickbookConnected) {
      if (props.data?.customer?.quickbookCustomer) {
        setCustomerQuickbookOption(SAVE_TYPES.EXIST);
        form.setFieldValue('quickbookCustomer', cast(props.data?.customer?.quickbookCustomer));
      } else {
        setCustomerQuickbookOption(SAVE_TYPES.NEW);
        form.setFieldValue('quickbookCustomer', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.editData]);
  useEffect(() => {
    if (tax) setCompanyTax(tax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    props.setOpen(open, needRefreshData);
  };

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_UNITS],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected) {
      if (customerQuickbookOption === SAVE_TYPES.NO) {
        form.setFieldValue('quickbookCustomerDisplayName', '');
        form.setFieldValue('quickbookCustomer', '');
      }

      if (customerQuickbookOption === SAVE_TYPES.NEW) {
        const quickbookCustomerDisplayName =
          props?.data?.customer?.type === CUSTOMER_TYPES_CONTACT
            ? generatePersonName(props?.data?.customer?.firstName, props?.data?.customer?.lastName)
            : props?.data?.customer?.name;
        form.setFieldValue('quickbookCustomerDisplayName', quickbookCustomerDisplayName);
      }

      if (customerQuickbookOption === SAVE_TYPES.EXIST) {
        dispatch(handleGetQuickbookCustomers())
          .unwrap()
          .catch((e) => {
            handleApiError(e);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuickbookOption]);
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xl'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addInvoice') : t('editInvoice')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form' className={cx('edit-invoice-container')}>
          <Box>
            <ClientInformation data={props.data} />
            <Box className='p-5'>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('property.name')}</InputLabel>
                  <TextField
                    name='name'
                    value={form.values?.name}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.name)}
                    helperText={form.errors?.name}
                  />
                </Box>
                {mode === DIALOG_MODE_EDIT && props.editData && (
                  <Box className='w-full'>
                    <InputLabel> {t('property.status')}</InputLabel>
                    <BaseSelect
                      name='status'
                      value={cast(form.values.status)}
                      options={statusOptions}
                      onChange={form.handleChange}
                    />
                  </Box>
                )}
              </BaseRow>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editInvoiceDialog.date')}</InputLabel>
                  <BaseDatePicker
                    placeholder={t('editInvoiceDialog.date')}
                    value={cast(form.values.date)}
                    onChange={(date) => {
                      if (date) form.setFieldValue('date', dayjs(date).format('YYYY-MM-DD'));
                    }}
                    error={Boolean(form.errors?.date)}
                    helperText={form.errors?.date}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editInvoiceDialog.expireDate')}</InputLabel>
                  <BaseDatePicker
                    placeholder={t('editInvoiceDialog.expireDate')}
                    value={cast(form.values.expireDate)}
                    onChange={(date) => {
                      if (date) form.setFieldValue('expireDate', dayjs(date).format('YYYY-MM-DD'));
                    }}
                    error={Boolean(form.errors?.expireDate)}
                    helperText={form.errors?.expireDate}
                  />
                </Box>
              </BaseRow>
              <Box className='mb-[20px]'>
                <InputLabel>{t('editInvoiceDialog.note')}</InputLabel>
                <BaseRichEditor
                  onCreateNew={(value: string) => form.setFieldValue('description', value)}
                  valueProps={form.values.description}
                  isError={Boolean(form.errors?.description)}
                  errorMessage={form.errors.description}
                />
              </Box>
              {quickbook?.isQuickbookConnected && !props.editData?.quickbookInvoiceNumber && (
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editInvoiceDialog.linkToQuickbookCustomer')}</InputLabel>
                    <BaseSelect
                      value={customerQuickbookOption}
                      options={newOrExistingOptionalOption}
                      onChange={(e) => {
                        setCustomerQuickbookOption(e.target.value);
                      }}
                    />
                  </Box>
                  <Box className='w-full'>
                    {customerQuickbookOption === SAVE_TYPES.NEW && (
                      <>
                        <InputLabel required={true}>{t('editInvoiceDialog.quickbookCustomerDisplayName')}</InputLabel>
                        <TextField
                          name='quickbookCustomerDisplayName'
                          value={cast(form.values?.quickbookCustomerDisplayName)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'customer.quickbookCustomerDisplayName'))}
                          helperText={getIn(form.errors, 'customer.quickbookCustomerDisplayName')}
                        />
                      </>
                    )}

                    {customerQuickbookOption === SAVE_TYPES.EXIST && (
                      <>
                        <InputLabel>{t('editInvoiceDialog.quickbookCustomer')}</InputLabel>
                        <BaseSelect
                          name='quickbookCustomer'
                          value={cast(form.values?.quickbookCustomer)}
                          options={quickbookCustomers}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'quickbookCustomer'))}
                          helperText={getIn(form.errors, 'quickbookCustomer')}
                        />
                      </>
                    )}
                  </Box>
                </BaseRow>
              )}
            </Box>
            <PaymentCalculation
              form={form}
              subtotal={cast(form.values.subtotal)}
              totalTax={companyTax}
              editData={props.editData}
              onChangeTax={setCompanyTax}
            />
          </Box>
          <InvoiceListItem
            form={form}
            editData={props.editData}
            companyTax={companyTax}
            jobId={props?.data?.id}
            mode={mode}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>

        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.setFieldValue('isPushOnQuickbook', false);
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
        {quickbook?.isQuickbookConnected && !props.editData?.quickbookInvoiceNumber && (
          <Button
            color='info'
            variant='contained'
            onClick={() => {
              form.setFieldValue('isPushOnQuickbook', true);
              form.handleSubmit();
            }}
          >
            {t('editInvoiceDialog.saveAndPushOnQuickbook')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditInvoiceDialog);
